import {
  AuthResponse,
  ThirdPartyLoginConnection,
  UserResponse,
} from '../py-generated/datalouder/third_party_login/types';
import { SendResetPasswordCommResponse } from '../py-generated/datalouder/user/types';
import { apiUrl, apiv2Url } from './config';
import {
  getPromise,
  handleJSONResponse,
  postOptionWithBody,
  postPromise,
} from './utils';

export const saveLoginOrRegisterResponseAndReturnUser = ({
  user,
  token,
}: AuthResponse): UserResponse => {
  localStorage.setItem('user', JSON.stringify({ ...user, authToken: token }));
  return user;
};

const saveUserAndReturnUser = user => {
  const currentUser = JSON.parse(localStorage.getItem('user'));
  localStorage.setItem('user', JSON.stringify({ ...currentUser, ...user }));
  return { ...currentUser, ...user };
};

function login(
  login: string,
  password: string,
  remember?: boolean
): Promise<UserResponse> {
  const options = postOptionWithBody({
    login,
    password,
    remember,
  });

  return fetch(`${apiv2Url}/user/login`, options)
    .then(handleJSONResponse())
    .then(saveLoginOrRegisterResponseAndReturnUser);
}

function logout(): void {
  localStorage.removeItem('user');
}

function register(
  user: {
    login: string;
    password: string;
    alias: string;
    publicName: string;
  },
  provider?: string,
  thirdPartyToken?: string
): Promise<UserResponse> {
  const { login, password, alias, publicName } = user;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      login,
      password,
      alias,
      publicName,
      provider,
      token: thirdPartyToken,
    }),
  };

  return fetch(`${apiv2Url}/user/register`, options)
    .then(handleJSONResponse())
    .then(saveLoginOrRegisterResponseAndReturnUser);
}

function updateUser(alias: string, login: string): Promise<UserResponse> {
  return postPromise(`${apiv2Url}/user/self`, { alias, login }).then(
    saveUserAndReturnUser
  );
}

function refresh(): Promise<UserResponse> {
  return getPromise(`${apiv2Url}/user/self`, {}).then(saveUserAndReturnUser);
}

function resendVerificationCommunicate(): Promise<void> {
  return postPromise(`${apiv2Url}/user/resend-login-confirm`, {});
}

function verifyLogin(userId: number, token: string): Promise<void> {
  return postPromise(`${apiv2Url}/user/confirm-login`, {
    userId,
    token,
  });
}

function sendResetPassword(
  login: string
): Promise<SendResetPasswordCommResponse> {
  return postPromise<{ login: string }, SendResetPasswordCommResponse>(
    `${apiv2Url}/user/send-reset-pass`,
    { login }
  );
}

function resetPassword(
  login: string,
  token: string,
  password: string
): Promise<void> {
  return postPromise(`${apiv2Url}/user/reset-pass`, {
    login,
    token,
    password,
  });
}

const profilePermission = (): Promise<any> =>
  getPromise(`${apiUrl}/user/profilePermission`, {});

const updateProfile = (
  name: string,
  stockListPermission: string
): Promise<void> =>
  postPromise(`${apiv2Url}/user/profile`, { name, stockListPermission });

const getThirdPartyLoginConnection = (
  provider: ThirdPartyLoginConnection['provider'],
  token: string
): Promise<ThirdPartyLoginConnection> =>
  postPromise(`${apiv2Url}/third-party-login`, { provider, token });

export default {
  login,
  logout,
  register,
  updateUser,
  refresh,
  resendVerificationCommunicate,
  sendResetPassword,
  resetPassword,
  profilePermission,
  updateProfile,
  verifyLogin,
  getThirdPartyLoginConnection,
};
